import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, BankAccountPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import ModulePageHeader from '../../common/ModulePageHeader';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';
import {
  CashStatusChip,
  RowActionsMenu,
  StatStrip,
  fmtDate,
  getErrorMessage,
  unwrap,
  unwrapList,
} from './CashShared';

// Imprest funds: establish seeds the float (DR fund / CR bank), expenses draw
// it down (DRAFT -> APPROVED -> POSTED), replenish reimburses posted
// expenses from bank.
const PettyCashTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.CASH, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.CASH, ACTIONS.APPROVE);
  const canPost = can(RESOURCES.LEDGER.CASH, ACTIONS.APPROVE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createValues, setCreateValues] = useState({ fund_name: '', account_id: '' });
  const [saving, setSaving] = useState(false);
  const [establishDlg, setEstablishDlg] = useState({ open: false, bank_account_id: '', amount: '' });
  const [replenishDlg, setReplenishDlg] = useState({ open: false, bank_account_id: '', amount: '' });
  const [expenseDlg, setExpenseDlg] = useState({
    open: false, txn_date: new Date().toISOString().slice(0, 10),
    expense_account_id: '', amount: '', description: '', reference: '',
  });

  const loadFunds = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ledgerAPI.getPettyCashFunds();
      setFunds(unwrapList(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch petty cash funds'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadFunds(); }, [loadFunds]);

  const openFund = async (fund) => {
    setSelected(fund);
    setDetailLoading(true);
    try {
      const [sumRes, txnRes] = await Promise.all([
        ledgerAPI.getPettyCashFund(fund.fund_id),
        ledgerAPI.getPettyCashExpenses(fund.fund_id),
      ]);
      setSummary(unwrap(sumRes));
      setTxns(unwrapList(txnRes));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch fund detail'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = () => {
    if (selected) openFund(selected);
    loadFunds();
  };

  const submitCreate = async () => {
    if (!String(createValues.fund_name ?? '').trim()) {
      showSnackbar('Fund name is required', 'error');
      return;
    }
    if (!createValues.account_id) {
      showSnackbar('Imprest GL account is required', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.createPettyCashFund({
        fund_name: createValues.fund_name.trim(),
        account_id: Number(createValues.account_id),
      });
      showSnackbar('Petty cash fund created', 'success');
      setShowCreate(false);
      setCreateValues({ fund_name: '', account_id: '' });
      loadFunds();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitEstablish = async () => {
    if (!establishDlg.bank_account_id || !(Number(establishDlg.amount) > 0)) {
      showSnackbar('Bank account and an amount greater than zero are required', 'error');
      return;
    }
    try {
      await ledgerAPI.establishPettyCashFund(selected.fund_id, {
        bank_account_id: Number(establishDlg.bank_account_id),
        amount: Number(establishDlg.amount),
      });
      showSnackbar('Float established', 'success');
      setEstablishDlg({ open: false, bank_account_id: '', amount: '' });
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitReplenish = async () => {
    if (!replenishDlg.bank_account_id) {
      showSnackbar('Bank account is required', 'error');
      return;
    }
    const payload = { bank_account_id: Number(replenishDlg.bank_account_id) };
    if (replenishDlg.amount) payload.amount = Number(replenishDlg.amount);
    try {
      await ledgerAPI.replenishPettyCashFund(selected.fund_id, payload);
      showSnackbar('Fund replenished', 'success');
      setReplenishDlg({ open: false, bank_account_id: '', amount: '' });
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitExpense = async () => {
    if (!expenseDlg.expense_account_id || !(Number(expenseDlg.amount) > 0)) {
      showSnackbar('Expense account and an amount greater than zero are required', 'error');
      return;
    }
    if (!String(expenseDlg.description ?? '').trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }
    const payload = {
      txn_date: expenseDlg.txn_date,
      expense_account_id: Number(expenseDlg.expense_account_id),
      amount: Number(expenseDlg.amount),
      description: expenseDlg.description.trim(),
    };
    if (expenseDlg.reference?.trim()) payload.reference = expenseDlg.reference.trim();
    try {
      await ledgerAPI.createPettyCashExpense(selected.fund_id, payload);
      showSnackbar('Expense recorded as DRAFT', 'success');
      setExpenseDlg({
        open: false, txn_date: new Date().toISOString().slice(0, 10),
        expense_account_id: '', amount: '', description: '', reference: '',
      });
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const runTxnWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this expense?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Expense ${pastTense} successfully`, 'success');
          refreshDetail();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const tiles = summary ? [
    { label: 'Float', value: formatAccountingAmount(summary.float) },
    { label: 'Expensed (posted)', value: formatAccountingAmount(summary.expensed) },
    { label: 'Replenished', value: formatAccountingAmount(summary.replenished) },
    { label: 'Cash on hand', value: formatAccountingAmount(summary.cash_on_hand) },
    { label: 'Unreplenished', value: formatAccountingAmount(summary.unreplenished) },
  ] : [];

  const exportFundsCsv = () => {
    const headers = ['Fund', 'GL Account', 'Float', 'Transactions', 'Status'];
    const rows = funds.map((f) => [
      f.fund_name || '', look.accountLabel(f.account_id), Number(f.float_amount) || 0,
      f._count?.transactions ?? 0, f.is_active ? 'Active' : 'Inactive',
    ]);
    downloadCsv('petty-cash-funds.csv', headers, rows);
  };

  const exportTxnsCsv = () => {
    const headers = ['Fund', 'Date', 'Kind', 'Description', 'Reference', 'Expense Account', 'Amount', 'Status'];
    const rows = txns.map((t) => [
      selected?.fund_name || '', t.txn_date || '', t.kind || '', t.description || '', t.reference || '',
      t.expense_account_id ? look.accountLabel(t.expense_account_id) : '', Number(t.amount) || 0, t.status || '',
    ]);
    downloadCsv(`petty-cash-${selected?.fund_id ?? 'txns'}.csv`, headers, rows);
  };

  return (
    <>
      <ModulePageHeader
        title="Petty Cash"
        subtitle="Imprest floats, cash expenses and bank replenishments"
        actions={[
          ...(canCreate ? [
            <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)}>
              New Fund
            </Button>,
          ] : []),
          <Button key="export" variant="outlined" startIcon={<DownloadIcon />} onClick={exportFundsCsv} disabled={!funds.length}>
            Export CSV
          </Button>,
        ]}
      />

      <Card>
        <CardContent>
          {funds.length === 0 && !loading ? (
            <EmptyRecordsState message="No petty cash funds yet." />
          ) : (
            <DataTable
              columns={['Fund', 'GL Account', 'Float', 'Txns', 'Status', 'Actions']}
              data={funds}
              loading={loading}
              emptyMessage="No petty cash funds found."
              minWidth={800}
              renderRow={(f) => (
                <TableRow key={f.fund_id} hover sx={{ cursor: 'pointer' }} onClick={() => openFund(f)}>
                  <TableCell><strong>{f.fund_name}</strong></TableCell>
                  <TableCell>{look.accountLabel(f.account_id)}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(f.float_amount)}</TableCell>
                  <TableCell>{f._count?.transactions ?? '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={f.is_active ? 'Active' : 'Inactive'} color={f.is_active ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button size="small" variant="outlined" startIcon={<WalletIcon />} onClick={() => openFund(f)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            />
          )}
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Button size="small" variant="text" startIcon={<RefreshIcon />} onClick={loadFunds}>Refresh</Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onClose={() => { setSelected(null); setSummary(null); setTxns([]); }} maxWidth="lg" fullWidth>
        <DialogTitle>{selected?.fund_name}</DialogTitle>
        <DialogContent>
          {detailLoading || !summary ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : (
            <>
              <StatStrip tiles={tiles} />
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
                {canPost && <Button size="small" variant="outlined" onClick={() => setEstablishDlg({ open: true, bank_account_id: '', amount: '' })}>Establish / Top Up</Button>}
                {canPost && <Button size="small" variant="outlined" onClick={() => setReplenishDlg({ open: true, bank_account_id: '', amount: '' })}>Replenish</Button>}
                {canCreate && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setExpenseDlg((p) => ({ ...p, open: true }))}>New Expense</Button>}
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportTxnsCsv} disabled={!txns.length}>
                  Export CSV
                </Button>
              </Box>
              <Typography variant="subtitle2" gutterBottom>Transactions</Typography>
              {txns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No transactions yet.</Typography>
              ) : (
                <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Kind</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Expense Account</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {txns.map((t) => (
                        <TableRow key={t.txn_id}>
                          <TableCell>{fmtDate(t.txn_date)}</TableCell>
                          <TableCell>{t.kind}</TableCell>
                          <TableCell>{t.description}{t.reference ? ` (${t.reference})` : ''}</TableCell>
                          <TableCell>{t.expense_account_id ? look.accountLabel(t.expense_account_id) : '-'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(t.amount)}</TableCell>
                          <TableCell>
                            <CashStatusChip status={t.status} />
                          </TableCell>
                          <TableCell>
                            <RowActionsMenu
                              items={[
                                t.kind === 'EXPENSE' && t.status === 'DRAFT' && canApprove && {
                                  icon: <ApproveIcon fontSize="small" color="primary" />, label: 'Approve',
                                  onClick: () => runTxnWorkflow('Approve', 'approved', () => ledgerAPI.approvePettyCashExpense(t.txn_id)),
                                },
                                t.kind === 'EXPENSE' && t.status === 'APPROVED' && canPost && {
                                  icon: <PostIcon fontSize="small" color="primary" />, label: 'Post',
                                  onClick: () => runTxnWorkflow('Post', 'posted', () => ledgerAPI.postPettyCashExpense(t.txn_id)),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelected(null); setSummary(null); setTxns([]); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Petty Cash Fund"
        saving={saving}
        onSubmit={submitCreate}
        values={createValues}
        onChange={(key, value) => setCreateValues((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'fund_name', label: 'Fund Name', required: true, md: 6 },
          { key: 'account_id', label: 'Imprest GL Account', type: 'picker', picker: AccountPicker, required: true, md: 6 },
        ]}
      />

      <LedgerFormDialog
        open={establishDlg.open}
        onClose={() => setEstablishDlg({ open: false, bank_account_id: '', amount: '' })}
        title={`Establish float - ${selected?.fund_name || ''}`}
        onSubmit={submitEstablish}
        values={establishDlg}
        onChange={(key, value) => setEstablishDlg((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'bank_account_id', label: 'Funding Bank Account', type: 'picker', picker: BankAccountPicker, required: true, md: 6 },
          { key: 'amount', label: 'Amount', type: 'number', required: true, min: 0.01, md: 6 },
        ]}
      />

      <LedgerFormDialog
        open={replenishDlg.open}
        onClose={() => setReplenishDlg({ open: false, bank_account_id: '', amount: '' })}
        title={`Replenish - ${selected?.fund_name || ''}`}
        onSubmit={submitReplenish}
        values={replenishDlg}
        onChange={(key, value) => setReplenishDlg((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'bank_account_id', label: 'Reimbursing Bank Account', type: 'picker', picker: BankAccountPicker, required: true, md: 6 },
          { key: 'amount', label: 'Amount (blank = all unreplenished)', type: 'number', min: 0.01, md: 6 },
        ]}
      />

      <LedgerFormDialog
        open={expenseDlg.open}
        onClose={() => setExpenseDlg((p) => ({ ...p, open: false }))}
        title={`New expense - ${selected?.fund_name || ''}`}
        onSubmit={submitExpense}
        values={expenseDlg}
        onChange={(key, value) => setExpenseDlg((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'txn_date', label: 'Date', type: 'date', required: true, md: 6 },
          { key: 'amount', label: 'Amount', type: 'number', required: true, min: 0.01, md: 6 },
          { key: 'expense_account_id', label: 'Expense Account', type: 'picker', picker: AccountPicker, required: true, md: 6 },
          { key: 'reference', label: 'Reference', md: 6 },
          { key: 'description', label: 'Description', required: true, md: 12 },
        ]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default PettyCashTab;
